import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { fetchCurrencyList, fetchCurrencyRates } from 'actions';

import {
  getLocaleInDBFormat,
  isNumber,
  getLocaleInURLFormat,
  parseDate,
  formatParsedDate,
} from 'utils';

import Alert from 'components/Alert';
import Icon from 'components/commons/Icon';
import { ParagraphSmall } from 'components/Typography';
// import Select from 'components/commons/Select';
import CurrencyConverterDropdown from 'components/CurrencyConverterDropdown';
import { SectionTitle } from 'components/SectionTitle';
import { convertorTypes } from 'constants/currency';
import ContentPlaceholder from 'components/ContentPlaceholder';
import { ConversionNumbersList } from 'components/ConversionNumbersList';

import {
  HeaderFilters,
  ConverterLink,
  ConverterInput,
  InputWrapper,
  Header,
  Wrapper,
  AlertTitle,
  ConverterWrapper,
  ReverseButton,
  StyledSelect,
} from './styles';

const options = [
  {
    label: 'midbank',
    value: 'midbank',
  },
  {
    label: 'bestbank',
    value: 'bestbank',
  },
  {
    label: 'nbu',
    value: 'nbu',
  },
  {
    label: 'interbank',
    value: 'interbank',
  },
];

const CurrencyConverter = ({
  currenciesItems,
  currenciesIsFetching,
  dispatch,
  intl,
  currencyRates,
  optionType,
  page,
}) => {
  const { currency, date } = useParams();
  const { locale } = intl;

  const [focus, setFocus] = useState({ left: false, right: false });
  const [isFetchingRates, setFetchingRatesStatus] = useState(true);
  const [type, setType] = useState(optionType || options[0].value);
  const [sourceValue, setSourceValue] = useState(100);
  const [targetValue, setTargetValue] = useState(0);
  const [rates, setRates] = useState({ sourceRate: 0, targetRate: 0 });
  const [source, setSource] = useState({
    code: currency?.toUpperCase() || 'USD',
    type: 'money',
  });
  const [target, setTarget] = useState({
    code: 'UAH',
    type: 'money',
  });

  useEffect(() => {
    const fetchData = async () => {
      setFetchingRatesStatus(true);
      return dispatch(
        fetchCurrencyRates({
          cur2: target.code.toLowerCase(),
          cur2_type: target.type,
          cur1: source.code.toLowerCase(),
          cur1_type: source.type,
          locale: getLocaleInDBFormat(intl.locale),
          date: date ? parseDate(date)?.format('DD-MM-YYYY') || '' : '',
        })
      ).finally(() => setFetchingRatesStatus(false));
    };

    if (target.code !== source.code) {
      fetchData().catch(console.error);
    }
  }, [source, target, date]);

  useEffect(() => {
    if (!currencyRates) {
      return;
    }
    const [sourceRate, targetRate] = currencyRates;

    const isCrypto = target.type === 'crypto' || source.type === 'crypto';
    const currencyType = isCrypto ? 'midbank' : type;

    if (!sourceRate?.[currencyType]?.sell || !targetRate?.[currencyType]?.buy) {
      setTargetValue('-');
      return;
    }

    const computedValue =
      (sourceRate[currencyType]?.sell?.val * sourceValue) /
      targetRate[currencyType]?.buy?.val;
    setRates({
      sourceRate: sourceRate[currencyType]?.sell?.val,
      targetRate: targetRate[currencyType]?.buy?.val,
    });
    setTargetValue(computedValue);
  }, [sourceValue, currencyRates, type]);

  useEffect(() => {
    if (currency) {
      const code = currency.toUpperCase();
      setSource({
        code,
        type: source.type,
      });
    }
  }, [currency]);

  const formattedTargetValue = useMemo(() => {
    if (!targetValue) {
      return 0;
    }

    return intl.formatNumber(targetValue, {
      maximumFractionDigits: target.type === convertorTypes.CRYPTO ? 8 : 2,
    });
  }, [targetValue, target.type]);

  const onSelectSourceDropdownOption = value => {
    const [code, type] = value.split('-');
    setSource({
      code,
      type,
    });
  };

  const onSelectTargetDropdownOption = value => {
    const [code, type] = value.split('-');

    setTarget({
      code,
      type,
    });
  };

  const fetchCurrenciesByScroll = type => {
    dispatch(
      fetchCurrencyList({
        type,
        cpp: 15,
        start: currenciesItems[type].length,
        locale: getLocaleInDBFormat(intl.locale),
      })
    );
  };

  const reverseCurrencies = () => {
    if (targetValue) {
      setSource(target);
      setTarget(source);
      setSourceValue(targetValue);
    }
  };

  return (
    <Wrapper>
      <Header>
        <SectionTitle
          title={
            date ? 'currencyConverter.title.date' : 'currencyConverter.title'
          }
          titleValue={formatParsedDate(date, 'DD MMMM YYYY', locale)}
        />
        <StyledSelect
          options={options}
          onChange={setType}
          value={type}
          titleExtractor={value => `currencyConverter.converterType.${value}`}
          labelExtractor={label => `currencyConverter.converterType.${label}`}
        />
      </Header>
      {isFetchingRates ? (
        <ContentPlaceholder
          style={{
            height: '50px',
            width: '100%',
            marginBottom: '12px',
          }}
        />
      ) : (
        <HeaderFilters>
          <ConverterWrapper>
            <InputWrapper focus={focus.left}>
              <ConverterInput
                type="text"
                value={sourceValue}
                onChange={e => setSourceValue(e.target.value)}
                onFocus={() => setFocus({ ...focus, left: true })}
                onBlur={() => setFocus({ ...focus, left: false })}
              />
              <CurrencyConverterDropdown
                currencyInfo={source}
                items={currenciesItems}
                loading={currenciesIsFetching}
                onSelectOption={onSelectSourceDropdownOption}
                onScrollEnd={fetchCurrenciesByScroll}
                optionType={optionType}
              />
            </InputWrapper>

            <ReverseButton
              type="button"
              aria-label="reverse"
              onClick={reverseCurrencies}
            >
              <Icon name="arrows" style={{ transition: 'transform 0.3s' }} />
            </ReverseButton>

            <InputWrapper focus={focus.right}>
              <ConverterInput
                type="text"
                value={
                  source.code !== target.code
                    ? formattedTargetValue
                    : intl.formatMessage({ id: 'empty' })
                }
                onFocus={() => setFocus({ ...focus, right: true })}
                onBlur={() => setFocus({ ...focus, right: false })}
              />

              <CurrencyConverterDropdown
                currencyInfo={target}
                items={currenciesItems}
                loading={currenciesIsFetching}
                onSelectOption={onSelectTargetDropdownOption}
                onScrollEnd={fetchCurrenciesByScroll}
                optionType={optionType}
              />
            </InputWrapper>
          </ConverterWrapper>
        </HeaderFilters>
      )}

      {!isNumber(targetValue) && (
        <Alert type="danger" outline>
          <AlertTitle>
            <FormattedMessage id="currencyConverter.noData.alert.title" />
          </AlertTitle>
          <ParagraphSmall>
            <FormattedMessage id="currencyConverter.noData.alert.description" />
          </ParagraphSmall>
        </Alert>
      )}

      <ConverterLink
        href={`${getLocaleInURLFormat(intl.locale)}/currency/converter/`}
      >
        <FormattedMessage id="currencyConverter.full" />
        <Icon name="vectorStroke" style={{ marginLeft: '8px' }} />
      </ConverterLink>

      {page === 'homepage' && currency && (
        <ConversionNumbersList rates={rates} source={source} target={target} />
      )}
    </Wrapper>
  );
};

const mapStateToProps = state => ({
  currenciesItems: state.convertor.currenciesItems,
  currencyRates: state.convertor.currencyRates,
});

export default connect(mapStateToProps)(injectIntl(CurrencyConverter));

CurrencyConverter.propTypes = {
  currenciesItems: PropTypes.shape({
    all: PropTypes.arrayOf(PropTypes.object),
    money: PropTypes.arrayOf(PropTypes.object),
    crypto: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  currenciesIsFetching: PropTypes.bool,
  intl: PropTypes.shape({
    locale: PropTypes.string,
    formatMessage: PropTypes.func,
    formatNumber: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  currencyRates: PropTypes.arrayOf(PropTypes.shape({})),
  match: PropTypes.shape({
    params: PropTypes.objectOf(PropTypes.string).isRequired,
  }),

  optionType: PropTypes.string,

  dispatch: PropTypes.func.isRequired,
  page: PropTypes.string,
};

CurrencyConverter.defaultProps = {
  optionType: null,
  match: null,
  currencyRates: null,
  location: null,
  currenciesIsFetching: null,
  page: null,
};
